import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail, Facebook, Instagram, Printer } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../mock';

const Footer = () => {
  const { language } = useLanguage();

  const quickLinks = {
    en: [
      { label: 'Auto Insurance', path: '/products/auto' },
      { label: 'Home Insurance', path: '/products/home' },
      { label: 'Business Insurance', path: '/products/business' },
      { label: 'Life Insurance', path: '/products/life' },
      { label: 'Get a Quote', path: '/quote/auto' },
      { label: 'Make a Payment', path: '/payment' }
    ],
    es: [
      { label: 'Seguro de Auto', path: '/products/auto' },
      { label: 'Seguro de Casa', path: '/products/home' },
      { label: 'Seguro de Negocio', path: '/products/business' },
      { label: 'Seguro de Vida', path: '/products/life' },
      { label: 'Obtener Cotización', path: '/quote/auto' },
      { label: 'Hacer un Pago', path: '/payment' }
    ]
  };

  const resources = {
    en: [
      { label: 'ID Card Request', path: '/resources/id-card' },
      { label: 'Claims', path: '/resources/claims' },
      { label: 'FAQ', path: '/resources/faq' },
      { label: 'Blog', path: '/blog' },
      { label: 'Contact Us', path: '/contact' }
    ],
    es: [
      { label: 'Solicitud de Tarjeta de ID', path: '/resources/id-card' },
      { label: 'Reclamos', path: '/resources/claims' },
      { label: 'Preguntas Frecuentes', path: '/resources/faq' },
      { label: 'Blog', path: '/blog' },
      { label: 'Contáctenos', path: '/contact' }
    ]
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Section with Social Media */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">
                {companyInfo.name}
              </h3>
              <p className="text-sm text-gray-400">
                {language === 'en' ? 'About' : 'Sobre Nosotros'}
              </p>
            </div>
            <p className="text-gray-400 leading-relaxed">
              {language === 'en'
                ? 'Rooted in our Hispanic heritage and committed to serving a diverse clientele.'
                : 'Arraigados en nuestra herencia hispana y comprometidos a servir a una clientela diversa.'}
            </p>
            
            {/* Follow Us Section */}
            <div>
              <h4 className="text-white font-semibold mb-3">
                {language === 'en' ? 'Follow Us' : 'Síguenos'}
              </h4>
              <div className="flex gap-3 flex-wrap">
                <a 
                  href={companyInfo.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                  title="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href={companyInfo.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                  title="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href={companyInfo.socialMedia.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 hover:bg-black rounded-full flex items-center justify-center transition-colors"
                  aria-label="X"
                  title="X (Twitter)"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a 
                  href={companyInfo.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                  aria-label="YouTube"
                  title="YouTube"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6">
              {language === 'en' ? 'Quick Links' : 'Enlaces Rápidos'}
            </h3>
            <ul className="space-y-3">
              {quickLinks[language].map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="hover:text-blue-400 transition-colors inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6">
              {language === 'en' ? 'Resources' : 'Recursos'}
            </h3>
            <ul className="space-y-3">
              {resources[language].map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="hover:text-blue-400 transition-colors inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6">
              {language === 'en' ? 'Contact Us' : 'Contáctenos'}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <a href={`tel:${companyInfo.phone}`} className="hover:text-blue-400 transition-colors block">
                    {companyInfo.phone}
                  </a>
                </div>
              </li>
              {companyInfo.fax && (
                <li className="flex items-start gap-3">
                  <Printer className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-gray-400 text-sm">
                      {language === 'en' ? 'Fax:' : 'Fax:'} {companyInfo.fax}
                    </span>
                  </div>
                </li>
              )}
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <a
                    href="https://maps.google.com/?q=400+Stonebrook+Pkwy+Unit+503+Frisco+TX+75036"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors"
                  >
                    {companyInfo.address}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <a href={`mailto:${companyInfo.email}`} className="hover:text-blue-400 transition-colors">
                    {companyInfo.email}
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>
              Copyright © 2025 – 2053 {companyInfo.legalName || companyInfo.name}
              {companyInfo.npn && (
                <span className="ml-2">| NPN: {companyInfo.npn}</span>
              )}
              {' | '}
              <Link to="/terms" className="hover:text-blue-400 transition-colors ml-1">
                {language === 'en' ? 'Terms' : 'Términos'}
              </Link>
              <span className="mx-2">&</span>
              <Link to="/privacy" className="hover:text-blue-400 transition-colors">
                {language === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;